import axios from '@/utils/axios';
import type {
  CommunicationLog,
  CommunicationLogsListResponse,
  CommunicationLogsMeta,
  GetCommunicationLogsParams,
} from '../types';

type UnknownRecord = Record<string, unknown>;

const toRecord = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as UnknownRecord)
    : {};

const firstDefined = (...values: unknown[]) =>
  values.find((value) => value !== undefined && value !== null);

const toString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const toPrimaryId = (value: unknown, fallback: string): number | string => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return fallback;

    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? trimmed : parsed;
  }

  return fallback;
};

const toOptionalId = (value: unknown): number | string | null => {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    const parsed = Number(trimmed);
    return Number.isNaN(parsed) ? trimmed : parsed;
  }

  return null;
};

const toArray = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value === undefined || value === null || value === '') {
    return [];
  }

  return [value];
};

const extractCollection = (payload: unknown): unknown[] => {
  const root = toRecord(payload);
  const data = toRecord(root.data);

  const candidates = [
    root.data,
    data.data,
    root.results,
    root.items,
    root.logs,
    data.results,
    data.items,
    data.logs,
  ];

  return (candidates.find(Array.isArray) as unknown[] | undefined) ?? [];
};

const normalizeMeta = (payload: unknown): CommunicationLogsMeta => {
  const root = toRecord(payload);
  const data = toRecord(root.data);
  const meta = toRecord(root.meta);
  const dataMeta = toRecord(data.meta);

  return {
    total:
      toNumber(
        firstDefined(meta.total, dataMeta.total, root.total, data.total)
      ) ?? null,
    limit:
      toNumber(
        firstDefined(meta.limit, dataMeta.limit, root.limit, data.limit)
      ) ?? null,
    offset:
      toNumber(
        firstDefined(meta.offset, dataMeta.offset, root.offset, data.offset)
      ) ?? null,
  };
};

const extractEntity = (payload: unknown): unknown => {
  const root = toRecord(payload);
  const data = toRecord(root.data);

  return (
    data.data ??
    data.communicationLog ??
    data.communication_log ??
    root.data ??
    root
  );
};

export const normalizeCommunicationLog = (
  value: unknown,
  fallbackIndex: number = 0
): CommunicationLog => {
  const item = toRecord(value);

  return {
    id: toPrimaryId(item.id, `communication-log-${fallbackIndex}`),
    dispatchId: toString(firstDefined(item.dispatch_id, item.dispatchId)),
    channel: toString(item.channel) ?? '',
    provider: toString(item.provider) ?? '',
    status: toString(item.status) ?? '',
    organisationId: toOptionalId(
      firstDefined(item.organisation_id, item.organisationId)
    ),
    userId: toOptionalId(firstDefined(item.user_id, item.userId)),
    recipientEmail: toString(
      firstDefined(item.recipient_email, item.recipientEmail)
    ),
    recipientType: toString(
      firstDefined(item.recipient_type, item.recipientType)
    ),
    recipientIdentifier: toString(
      firstDefined(item.recipient_identifier, item.recipientIdentifier)
    ),
    tokenCount: toNumber(firstDefined(item.token_count, item.tokenCount)),
    tokenHashes: toArray(firstDefined(item.token_hashes, item.tokenHashes)),
    to: toArray(item.to),
    cc: toArray(item.cc),
    bcc: toArray(item.bcc),
    subject: toString(item.subject),
    htmlBody: toString(firstDefined(item.html_body, item.htmlBody)),
    textBody: toString(firstDefined(item.text_body, item.textBody)),
    providerPayload: firstDefined(item.provider_payload, item.providerPayload),
    providerResponse: firstDefined(
      item.provider_response,
      item.providerResponse
    ),
    messageId: toString(firstDefined(item.message_id, item.messageId)),
    errorMessage: toString(firstDefined(item.error_message, item.errorMessage)),
    sentAt: toString(firstDefined(item.sent_at, item.sentAt)),
    failedAt: toString(firstDefined(item.failed_at, item.failedAt)),
    createdAt: toString(firstDefined(item.created_at, item.createdAt)),
    updatedAt: toString(firstDefined(item.updated_at, item.updatedAt)),
  };
};

export async function getUserCommunicationLogs(
  userId: number | string,
  params?: GetCommunicationLogsParams
): Promise<CommunicationLogsListResponse> {
  const response = await axios.get(`/admin/users/${userId}/communications`, {
    params,
  });

  const payload = response?.data ?? {};
  const collection = extractCollection(payload);

  return {
    data: collection.map((item, index) =>
      normalizeCommunicationLog(item, index)
    ),
    meta: normalizeMeta(payload),
  };
}

export async function getCommunicationLog(
  communicationLogId: number | string
): Promise<CommunicationLog> {
  const response = await axios.get(
    `/admin/communications/${communicationLogId}`
  );

  return normalizeCommunicationLog(extractEntity(response?.data ?? {}));
}
